import React from 'react';
import { CUSTOM_RESULT_KEYS, MODULE_KEYS } from 'constants/constants';
import { EPD_DICTIONARY_CODES, INDICATOR_GROUP_CODES } from 'services/api/constants';
import styled from 'styled-components';

import { YES_NO } from '../../constants';
import LcaResultsGridAdditionalConstruction from '../../grids/lca-results/LcaResultsGridAdditionalConstruction';
import LcaResultsGridAdditionalMandatory from '../../grids/lca-results/LcaResultsGridAdditionalMandatory';
import LcaResultsGridAdditionalVoluntary from '../../grids/lca-results/LcaResultsGridAdditionalVoluntary';
import LcaResultsGridCoreConstruction from '../../grids/lca-results/LcaResultsGridCoreConstruction';
import LcaResultsGridOutputFlowConstruction from '../../grids/lca-results/LcaResultsGridOutputFlowConstruction';
import LcaResultsGridResourceConstruction from '../../grids/lca-results/LcaResultsGridResourceConstruction';
import LcaResultsGridWasteConstruction from '../../grids/lca-results/LcaResultsGridWasteConstruction';
import LcaCustomBestWorstResultsGrid from '../../grids/lca-results/custom/LcaCustomBestWorstResultsGrid';
import LcaCustomBiogenicResultsGrid from '../../grids/lca-results/custom/LcaCustomBiogenicResultsGrid';
import LcaCustomAdditionalResultsGrid from '../../grids/lca-results/custom/LcaCustomAdditionalResultsGrid';
import VersionHistoryExpandableGridWrapper from '../VersionHistoryExpandableGridWrapper';
import VersionHistorySection from '../VersionHistorySection';
import VersionHistorySummaryGrid, { SummaryGridRow, VersionHistorySummaryGridSkeleton } from '../VersionHistorySummaryGrid';
import {
  EpdVersionHistoryData,
  useEpdVersionHistoryDataOrWhileLoadingThenNull,
} from '../utils/EpdVersionHistoryDataContext';
import VersionHistoryLcaCustomResultGrid from './VersionHistoryLcaCustomResultGrid';
import { useEpdLcaCustomResultGridData } from 'services/api/queries';
import { LcaImportContextProvider } from '../../grids/lca-results/util/LcaImportContext';

const VersionHistoryLcaResultsSection: React.FC<{ expandedModeOnly?: boolean }> = ({ expandedModeOnly }) => {
  const data = useEpdVersionHistoryDataOrWhileLoadingThenNull();
  const additionalVoluntaryRows = getAdditionalVoluntaryRows(data);
  return (
    <VersionHistorySection title="Environmental performance">
      {data ? (
        <div>
          <LcaImportContextProvider>
            <VersionHistoryExpandableGridWrapper
              title="Mandatory environmental performance indicators according to EN 15804"
              expandedModeOnly={expandedModeOnly}
            >
              <LcaResultsGridCoreConstruction
                epdVersionId={data.epdVersionId}
                rows={data.lcaResults.lcaResults?.filter((x) => x.indicatorGroupCode === INDICATOR_GROUP_CODES.CORE15804)}
                isAdditionalDisclaimerVisible={data.infrastructureAndCapitalGoods?.includedInProcesses === YES_NO.YES}
                disabled
                preferEnabledAppearanceEvenWhenDisabled
              />
            </VersionHistoryExpandableGridWrapper>

            <VersionHistoryExpandableGridWrapper
              title="Additional mandatory environmental performance indicators"
              expandedModeOnly={expandedModeOnly}
            >
              <LcaResultsGridAdditionalMandatory
                epdVersionId={data.epdVersionId}
                rows={data.lcaResults.lcaResults?.filter(
                  (x) => x.indicatorGroupCode === INDICATOR_GROUP_CODES.ADDITIONALMANDATORY
                )}
                disabled
                preferEnabledAppearanceEvenWhenDisabled
              />
            </VersionHistoryExpandableGridWrapper>

            <VersionHistoryExpandableGridWrapper
              title="Additional voluntary environmental performance indicators according to EN 15804"
              expandedModeOnly={expandedModeOnly}
            >
              <LcaResultsGridAdditionalConstruction
                epdVersionId={data.epdVersionId}
                rows={data.lcaResults.lcaResults?.filter(
                  (x) => x.indicatorGroupCode === INDICATOR_GROUP_CODES.ADDITIONALVOLUNTARY15804
                )}
                isAdditionalDisclaimerVisible={data.infrastructureAndCapitalGoods?.includedInProcesses === YES_NO.YES}
                disabled
                preferEnabledAppearanceEvenWhenDisabled
              />
            </VersionHistoryExpandableGridWrapper>

            <VersionHistoryExpandableGridWrapper
              title="Additional voluntary environmental performance indicators"
              expandedModeOnly={expandedModeOnly}
              maximizableContent={
                <LcaResultsGridAdditionalVoluntary
                  epdVersionId={data.epdVersionId}
                  justificationForInclusion={data.lcaSpecification?.justificationForInclusion}
                  disabled
                  preferEnabledAppearanceEvenWhenDisabled
                />
              }
              additionalContent={
                additionalVoluntaryRows.length > 0 && <VersionHistorySummaryGrid rows={additionalVoluntaryRows} />
              }
            />

            <VersionHistoryExpandableGridWrapper
              title="Resource use indicators according to EN 15804"
              expandedModeOnly={expandedModeOnly}
            >
              <LcaResultsGridResourceConstruction
                epdVersionId={data.epdVersionId}
                rows={data.lcaResults.lcaResults.filter((x) => x.indicatorGroupCode === INDICATOR_GROUP_CODES.RESOURCE15804)}
                disabled
                preferEnabledAppearanceEvenWhenDisabled
              />
            </VersionHistoryExpandableGridWrapper>

            <VersionHistoryExpandableGridWrapper
              title="Waste indicators according to EN 15804"
              expandedModeOnly={expandedModeOnly}
            >
              <LcaResultsGridWasteConstruction
                epdVersionId={data.epdVersionId}
                rows={data.lcaResults.lcaResults.filter(
                  (x: any) => x.indicatorGroupCode === INDICATOR_GROUP_CODES.WASTE15804
                )}
                disabled
                preferEnabledAppearanceEvenWhenDisabled
              />
            </VersionHistoryExpandableGridWrapper>

            <VersionHistoryExpandableGridWrapper
              title="Output flow indicators according to EN 15804"
              expandedModeOnly={expandedModeOnly}
            >
              <LcaResultsGridOutputFlowConstruction
                epdVersionId={data.epdVersionId}
                rows={data.lcaResults.lcaResults.filter(
                  (x: any) => x.indicatorGroupCode === INDICATOR_GROUP_CODES.OUTPUTFLOW15804
                )}
                disabled
                preferEnabledAppearanceEvenWhenDisabled
              />
            </VersionHistoryExpandableGridWrapper>

            {data.lcaCustomResults.map((customResult) => {
              const resultTypeEntry = customResult.resultType
                ? data.dictionaries.find(
                    (d) =>
                      d.dictionaryCode === EPD_DICTIONARY_CODES.ADDITIONAL_LCA_RESULTS_TYPE &&
                      d.key === Number(customResult.resultType)
                  )
                : null;
              const rows = useEpdLcaCustomResultGridData(customResult.id).data;

              const isTableFilled =
                rows?.some((row: any) =>
                  MODULE_KEYS.some((key) => row[key] !== null && row[key] !== undefined && row[key] !== '')
                ) || customResult.gridDisclaimers;
              const title = resultTypeEntry ? resultTypeEntry.value : 'Additional environmental performance results';
              return (
                <VersionHistoryExpandableGridWrapper
                  key={customResult.id}
                  title={title}
                  hideMaximizeButton
                  expandedModeOnly={expandedModeOnly}
                >
                  <VersionHistoryLcaCustomResultGrid customResult={customResult} />

                  <CustomResultGridWrapper>
                    {[
                      CUSTOM_RESULT_KEYS.A4C4,
                      CUSTOM_RESULT_KEYS.MODELING,
                      CUSTOM_RESULT_KEYS.ELECTRICITY,
                      CUSTOM_RESULT_KEYS.BIOGAS,
                    ].includes(customResult.resultType) &&
                      isTableFilled && (
                        <LcaCustomAdditionalResultsGrid
                          epdVersionId={data.epdVersionId}
                          customResult={customResult}
                          disabled
                          preferEnabledAppearanceEvenWhenDisabled
                        />
                      )}
                    {customResult.resultType === CUSTOM_RESULT_KEYS.BIOGENIC && isTableFilled && (
                      <LcaCustomBiogenicResultsGrid
                        epdVersionId={data.epdVersionId}
                        customResultId={customResult.id}
                        disabled
                        preferEnabledAppearanceEvenWhenDisabled
                      />
                    )}
                    {[CUSTOM_RESULT_KEYS.BEST, CUSTOM_RESULT_KEYS.WORST].includes(customResult.resultType) &&
                      isTableFilled && (
                        <LcaCustomBestWorstResultsGrid
                          epdVersionId={data.epdVersionId}
                          customResultId={customResult.id}
                          disabled
                          preferEnabledAppearanceEvenWhenDisabled
                        />
                      )}
                  </CustomResultGridWrapper>
                </VersionHistoryExpandableGridWrapper>
              );
            })}
          </LcaImportContextProvider>
        </div>
      ) : (
        <VersionHistorySummaryGridSkeleton skeletonRowsCount={6} />
      )}
    </VersionHistorySection>
  );
};

export default VersionHistoryLcaResultsSection;

export const CustomResultGridWrapper = styled.div`
  margin-top: 16px;
`;

export function getAdditionalVoluntaryRows(data: EpdVersionHistoryData | null) {
  const possibleAdditionalVoluntaryRows: (SummaryGridRow | false)[] = [
    data?.lcaSpecification?.justificationForInclusion
      ? {
          ord: 1,
          name: 'Justification for inclusion',
          value: data?.lcaSpecification?.justificationForInclusion,
        }
      : false,
  ];

  const additionalVoluntaryRows = possibleAdditionalVoluntaryRows.filter((r) => r) as SummaryGridRow[];
  return additionalVoluntaryRows;
}
